import { useState, useEffect, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { applyPageSeo } from "../../lib/seo";
import { trackForgotPasswordViewed, trackPasswordResetRequested } from "../../lib/analytics";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    trackForgotPasswordViewed();
    return applyPageSeo({
      title: "Forgot Password",
      description: "Reset your password to regain access to your React Email Builder account.",
      path: "/auth/forgot-password",
      robots: "noindex,nofollow",
    });
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const response = await fetch("/api/auth/request-password-reset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          redirectTo: `${window.location.origin}/auth/reset-password`,
        }),
      });

      if (response.ok) {
        trackPasswordResetRequested();
        setSubmitted(true);
      } else {
        const data = await response.json().catch(() => null) as { message?: string } | null;
        setError(data?.message ?? "Something went wrong. Please try again.");
      }
    } catch {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="w-full max-w-sm">
        <div className="rounded-md border border-slate-200/80 bg-white/90 p-7 shadow-[0_24px_64px_rgba(15,23,42,0.08)] backdrop-blur">
          {submitted ? (
            <div className="text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-md bg-emerald-50">
                <svg viewBox="0 0 24 24" fill="none" className="h-7 w-7 text-emerald-600" aria-hidden="true">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h1 className="mt-5 text-2xl font-semibold tracking-tight text-slate-950">
                Check your email
              </h1>
              <p className="mt-3 text-sm leading-6 text-slate-600">
                If an account exists for <span className="font-medium text-slate-950">{email}</span>, we've sent a password reset link. Click the link in the email to set a new password.
              </p>
              <p className="mt-4 text-xs text-slate-400">
                Didn't receive the email? Check your spam folder or try again with a different address.
              </p>
              <div className="mt-6">
                <Link
                  to="/auth/login"
                  className="inline-flex items-center justify-center gap-2 rounded bg-slate-950 px-6 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                >
                  <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4" aria-hidden="true">
                    <path d="M15.833 10H4.167m0 0l4.166-4.167M4.167 10l4.166 4.167" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Back to sign in
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="text-center">
                <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-600">
                  Reset password
                </p>
                <h1 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
                  Forgot your password?
                </h1>
                <p className="mt-2 text-sm text-slate-500">
                  Enter the email address associated with your account and we'll send you a link to reset your password.
                </p>
              </div>

              <form className="mt-6 space-y-5" onSubmit={(event) => void handleSubmit(event)}>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700">
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    disabled={isSubmitting}
                    required
                    className="mt-1.5 block w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-500/10"
                  />
                </div>

                {error ? (
                  <p className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" role="alert">
                    {error}
                  </p>
                ) : null}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full rounded bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-950 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSubmitting ? "Sending reset link..." : "Send reset link"}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-slate-500">
          Remember your password?{" "}
          <Link to="/auth/login" className="font-semibold text-sky-600 transition hover:text-sky-500">
            Sign in
          </Link>
        </p>
      </div>
  );
}